'use client'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons'
import RoundedButton from '@/shared/components/buttons/rounded-button'
import type { BaseScreenProps } from '@/features/quiz/types'

// Bar proportions from the Paper ledger, covering months 2–12.
const monthlyBars = [
  { other: 114, pharmacy: 84, mobileOther: 93, mobilePharmacy: 68 },
  { other: 114, pharmacy: 84, mobileOther: 93, mobilePharmacy: 68 },
  { other: 143, pharmacy: 112, mobileOther: 116, mobilePharmacy: 91 },
  { other: 143, pharmacy: 112, mobileOther: 116, mobilePharmacy: 91 },
  { other: 143, pharmacy: 112, mobileOther: 116, mobilePharmacy: 91 },
  { other: 158, pharmacy: 126, mobileOther: 129, mobilePharmacy: 103 },
  { other: 158, pharmacy: 126, mobileOther: 129, mobilePharmacy: 103 },
  { other: 158, pharmacy: 126, mobileOther: 129, mobilePharmacy: 103 },
  { other: 172, pharmacy: 141, mobileOther: 140, mobilePharmacy: 115 },
  { other: 172, pharmacy: 141, mobileOther: 140, mobilePharmacy: 115 },
  { other: 172, pharmacy: 141, mobileOther: 140, mobilePharmacy: 115 },
]

const legend = [
  { label: 'NextClinic fee', color: 'bg-blue-600' },
  { label: 'Medication at pharmacy', color: 'bg-[#C7D7FB]' },
  { label: 'Other providers', color: 'bg-zinc-200' },
]

export default function WlConsistencyLedgerScreen({
  screenId,
  onChange,
}: Pick<BaseScreenProps, 'screenId' | 'onChange'>) {
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)
  const [continuing, setContinuing] = useState(false)
  const navigating = useRef(false)

  useEffect(() => {
    // Escape the quiz's animated ancestors so fixed positioning stays tied to
    // the viewport throughout screen transitions, including on mobile Safari.
    setPortalTarget(document.body)
  }, [])

  async function continueToBilling() {
    if (navigating.current) return
    navigating.current = true
    setContinuing(true)
    try {
      await onChange(
        screenId,
        { id: screenId, type: 'number', answer: 1 },
        true,
      )
    } finally {
      navigating.current = false
      setContinuing(false)
    }
  }

  return (
    <>
      <section
        className="mx-auto flex w-full max-w-3xl flex-col gap-5 font-sans text-[#0B0B0F] sm:gap-7"
        aria-labelledby="weight-loss-consistency-title"
        data-testid="weight-loss-consistency-ledger"
      >
        <h1
          id="weight-loss-consistency-title"
          className="font-display text-[30px] leading-9 tracking-tight sm:text-[40px] sm:leading-[48px]"
        >
          <span className="block font-extrabold">
            Weight loss is all about consistency.
          </span>
          <span className="block font-extralight">So is our price.</span>
        </h1>

        <div className="space-y-1.5 text-sm leading-5 text-zinc-700 sm:text-[17px] sm:leading-[26px]">
          <p>
            Your dose goes up over time. So does the price with other providers.
          </p>
          <p>
            <strong className="inline-block rounded bg-[#F2FF5E] px-1.5 text-[#0B0B0F]">
              $49.90 a month
            </strong>{' '}
            with NextClinic, at any dose.
          </p>
        </div>

        <div className="-mx-6 flex flex-col gap-4 border-y border-[#ECECEF] px-6 py-5 sm:mx-0 sm:gap-5 sm:rounded-xl sm:border sm:p-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <h2 className="text-[15px] font-semibold leading-5">
              Monthly cost
            </h2>
            <ul className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-[13px] font-medium leading-4 text-zinc-700 sm:gap-x-5">
              {legend.map(({ label, color }) => (
                <li key={label} className="flex items-center gap-1.5 sm:gap-2">
                  <span
                    aria-hidden="true"
                    className={`size-2.5 shrink-0 rounded-[3px] sm:size-3 ${color}`}
                  />
                  {label}
                </li>
              ))}
            </ul>
          </div>

          <div
            role="img"
            aria-label="Monthly costs from month 2 to month 12. The NextClinic care fee stays at $49.90 while pharmacy medication costs and other providers’ prices increase."
            className="relative flex h-40 items-end justify-between gap-1 sm:h-[200px] sm:gap-0"
          >
            <div
              aria-hidden="true"
              className="absolute inset-x-0 bottom-5 h-px bg-[#ECECEF] sm:bottom-6"
            />
            {monthlyBars.map((bar, index) => (
              <div
                key={index}
                aria-hidden="true"
                className="relative flex min-w-0 flex-1 flex-col items-center gap-1.5 sm:w-12 sm:flex-initial sm:gap-2"
              >
                <div className="flex w-full items-end gap-0.5 sm:w-auto sm:gap-1">
                  <div
                    className="h-[var(--mobile-height)] min-w-0 flex-1 rounded-t-[3px] bg-zinc-200 sm:h-[var(--desktop-height)] sm:w-[22px] sm:flex-none sm:rounded-t"
                    style={
                      {
                        '--mobile-height': `${bar.mobileOther}px`,
                        '--desktop-height': `${bar.other}px`,
                      } as React.CSSProperties
                    }
                  />
                  <div
                    className="flex h-[var(--mobile-height)] min-w-0 flex-1 flex-col justify-end rounded-t-[3px] bg-[#C7D7FB] sm:h-[var(--desktop-height)] sm:w-[22px] sm:flex-none sm:rounded-t"
                    style={
                      {
                        '--mobile-height': `${bar.mobilePharmacy}px`,
                        '--desktop-height': `${bar.pharmacy}px`,
                      } as React.CSSProperties
                    }
                  >
                    <div className="h-2 shrink-0 bg-blue-600 sm:h-2.5" />
                  </div>
                </div>
                <span className="whitespace-nowrap text-[11px] font-medium leading-[14px] text-zinc-500 sm:text-xs sm:leading-4">
                  {(index === 0 || index === monthlyBars.length - 1) && (
                    <span className="hidden sm:inline">Month </span>
                  )}
                  {index + 2}
                </span>
              </div>
            ))}
          </div>

          <dl className="flex flex-col divide-y divide-[#ECECEF] border-t border-[#ECECEF] pt-1 sm:grid sm:grid-cols-3 sm:gap-2 sm:divide-y-0 sm:pt-5">
            <div className="flex min-h-11 flex-wrap items-center justify-between gap-x-2 gap-y-1 py-2 text-zinc-500 sm:block sm:min-h-0 sm:space-y-1.5 sm:py-0">
              <dt className="text-[11px] font-semibold uppercase leading-[14px] tracking-wide sm:text-xs sm:leading-4">
                Other providers
              </dt>
              <dd className="font-display text-lg font-extrabold leading-6 tracking-tight sm:text-[22px] sm:leading-[30px]">
                Up to $900 a month
              </dd>
            </div>
            <div className="flex min-h-11 flex-wrap items-center justify-between gap-x-2 gap-y-1 py-2 sm:block sm:min-h-0 sm:space-y-1.5 sm:py-0">
              <dt className="text-[11px] font-semibold uppercase leading-[14px] tracking-wide sm:text-xs sm:leading-4">
                NextClinic
              </dt>
              <dd className="flex items-baseline gap-1.5 font-display text-lg font-extrabold leading-6 tracking-tight sm:text-[22px] sm:leading-[30px]">
                <span className="rounded bg-[#F2FF5E] px-1.5">$49.90</span>
                <span>+ pharmacy</span>
              </dd>
            </div>
            <div className="flex min-h-11 flex-wrap items-center justify-between gap-x-2 gap-y-1 py-2 sm:block sm:min-h-0 sm:space-y-1.5 sm:py-0">
              <dt className="text-[11px] font-semibold uppercase leading-[14px] tracking-wide sm:text-xs sm:leading-4">
                You save
              </dt>
              <dd className="font-display text-lg font-extrabold leading-6 tracking-tight sm:text-[22px] sm:leading-[30px]">
                Up to $1,900 a year
              </dd>
            </div>
          </dl>
        </div>

        <p className="flex items-center gap-2.5 text-sm leading-5 text-gray-900 sm:pl-1">
          <FontAwesomeIcon
            icon={faCircleXmark}
            className="size-4 shrink-0 text-gray-500"
          />
          Cancel anytime.
        </p>
      </section>

      {portalTarget &&
        createPortal(
          <div
            className="pointer-events-none fixed inset-x-0 bottom-0 z-20 bg-gradient-to-t from-white from-40% to-transparent pt-11"
            style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}
          >
            <div className="pointer-events-auto mx-auto max-w-3xl px-6 sm:px-8 lg:px-10">
              <RoundedButton
                type="button"
                className="min-h-11 whitespace-nowrap"
                disabled={continuing}
                loading={continuing}
                onClick={continueToBilling}
              >
                Start losing weight
              </RoundedButton>
            </div>
          </div>,
          portalTarget,
        )}
    </>
  )
}
